import { z } from 'zod'

import { QUANTITY_MAX } from 'app/utils/machines/planogram'

import { Product } from 'app/types/catalogue/product'
import { MachineBinding } from 'app/types/machines/machine'
import { BaseOrder } from 'app/types/operations/order'
import { OrderMachine } from 'app/types/shared/machine'

/** Type of machine possible in the planogram. */
export const TypeMachinePlanogram = z.enum([
    'DISTRIBUTOR',
    'FRIDGE_WEIGHT',
    'LOCKER_COLD',
    'LOCKER_DRY',
])

/** Enum planogram status. */
export const StatusPlanogram = z.enum(['ACTIVE', 'INACTIVE', 'PENDING'])

/** Enum status in the planogram list. */
export const StatusPlanogramList = z.enum([...StatusPlanogram.options, 'ALL'])

/** Enum planogram component mode. */
export const ModePlanogram = z.enum([
    'CREATE',
    'DETAILS',
    'PRODUCT_INFO',
    'TECHNICAL_DATAS',
])

export type ModePlanogram = z.infer<typeof ModePlanogram>

/** Enum planogram component actions. */
export const ActionPlanogram = z.enum([
    'LOCKER_OPEN_DOOR',
    'MANAGEMENT_TRAY',
    'MANAGEMENT_SELECTION',
    'MAPPING',
    'MOTOR',
    'PLANOGRAM_ORDER',
    'PRODUCT_CHANGE',
    'SIZE',
    'STOCK',
])

export type ActionPlanogram = z.infer<typeof ActionPlanogram>

/** Enum planogram selection error */
export const ErrorTypeSelection = z.enum([
    'EMPTY',
    'MAPPING',
    'MOTOR',
    'NAME',
    'PLANOGRAM_ORDER',
])

export const ArticlePlanogram = z.object({
    /** Position of a product in a selection. */
    position: z.number().min(1).max(QUANTITY_MAX), // Limited to 1 in lockers

    /** The article bbd. */
    bbd: z.string().date(),

    /** The weight of the article. */
    weight: z.number().optional(), // In g, required and only of product by
})

const BaseSelection = z.object({
    /** Selection ID. */
    id: z.number().nonnegative(),

    /** X position of the selection. */
    x_position: z.number().nonnegative(),

    /** Y position of the selection. */
    y_position: z.number().nonnegative(),

    /** Number of the selection. */
    mapping: z.string(),

    /** Dispensation speed of the selection. */
    dispensation_speed: z.number().nonnegative(),

    /** Motor activation time after dispensation. */
    motor_activation_time_after_dispensation: z.number().nonnegative(),

    /** Owner of the selection. */
    owner: z.number().nonnegative(),

    /** Recommended stock to fill. */
    recommended_stock: z.number().positive(),

    /** Maximum stock to fill. */
    maximum_stock: z.number().positive(),

    /** The number of articles to trigger a low stock alarm. */
    count_to_trigger_low_stock_alarm: z.number().positive(),

    /** The width of the selection in millimeters. Only in fridge weight */
    width_in_millimeters: z.number().min(120).max(610),

    /** Articles of the selection. */
    articles: ArticlePlanogram.array(),
})

const SelectionUnion = z.union([
    z.object({
        /** Product of the selection. */
        product: Product,
    }),
    z.object({
        /** Only in lockers. */
        order: BaseOrder.shape.id,
    }),
])

export const Selection = BaseSelection.and(SelectionUnion)
export type Selection = z.infer<typeof Selection>

export const Planogram = z.object({
    id: z.number().positive(),
    name: z.string().min(1),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
    activity_start: z.string().datetime({ offset: true }),
    status: StatusPlanogram,
    order: OrderMachine,
    machine: MachineBinding,
    address: z.string().min(1),
    selections: Selection.array().min(1),
})

export type Planogram = z.infer<typeof Planogram>

export const PlanogramShort = Planogram.omit({
    selections: true,
})

export type PlanogramShort = z.infer<typeof PlanogramShort>
